type ValuesOf<T extends Record<string, unknown>> = T[keyof T];

// Envoy Constants
export const X_ALLOW_COOKIES = 'X-Allow-Cookies';
export const X_BLOCK_COOKIES = 'X-Block-Cookies';
export const X_BLOCK_COOKIES_DEFAULT_VALUE = 'true';

// One Trust Constants
export const CONSENT_COOKIE_KEY = 'OptanonConsent';
export const ALERT_BOX_CLOSED_COOKIE_KEY = 'OptanonAlertBoxClosed';
export const CDN_GEOLOCATION_COOKIE_KEY = 'squareGeo';
/**
 * Categories defined by OneTrust that represent types of storage
 * or other functionality allowed on a user's device.
 */
export const COOKIE_GROUP_KEYS = {
  /**
   * Data or functionality required for an app to function or
   * provide necessary product features
   */
  strictlyNecessary: 'C0001',
  /**
   * Data or services like EventStream, Sentry, Firebase, etc that
   * helps us track crashes or performance
   */
  performanceAndAnalytics: 'C0002',
  /**
   * Data or services that support non-critical product features.
   */
  functionality: 'C0003',
  /**
   * Data or services used for marketing-related purposes.
   */
  retargetingOrAdvertising: 'C0004',
} as const;
/**
 * Cookie group identifiers used by OneTrust.
 * Available as constants on the {@link COOKIE_GROUP_KEYS} object.
 */
export type CookieGroupKeyValue = ValuesOf<typeof COOKIE_GROUP_KEYS>;
export const CONSENT_GROUP_COOKIE_SUBSTRING = 'groups=';
export const PREFERENCES_CHANGED_EVENT_KEY = 'oneTrustPreferencesChangedEvent';
// These countries are derived from One Trust's reference here:
// https://community.cookiepro.com/s/article/UUID-97c0d8a2-cf62-82b6-48e2-2c2bb4ef9c67?topicId=0TO1Q000000ssK9WAI
// and can also be found in their source file: https://cdn.cookielaw.org/scripttemplates/otSDKStub.js
export const EU_COUNTRIES: ReadonlyArray<string> = [
  'BE',
  'BG',
  'CZ',
  'DK',
  'DE',
  'EE',
  'IE',
  'GR',
  'ES',
  'FR',
  'IT',
  'CY',
  'LV',
  'LT',
  'LU',
  'HU',
  'MT',
  'NL',
  'AT',
  'PL',
  'PT',
  'RO',
  'SI',
  'SK',
  'FI',
  'SE',
  'GB',
  'HR',
  'LI',
  'NO',
  'IS',
];

const ALTERNATIVE_COMPLIANCE_LAWS: ReadonlyMap<
  string,
  Array<string>
> = new Map([['US', ['CA']]]);

export const DEFAULT_OPT_IN_REGIONS: ReadonlyArray<string> = ['US', 'CA'];

export const DEFAULT_OPT_OUT_REGIONS = EU_COUNTRIES;

// Interface definitions
interface OneTrust {
  getGeolocationData: () => {
    country?: string;
    state?: string;
    province?: string;
  };
}
declare global {
  /* eslint-disable no-var */
  var OneTrust: OneTrust;
  var OnetrustActiveGroups: string;
  /* eslint-enable no-var */
}

interface LocationHash {
  country?: string;
  state?: string;
}

export default class SqOneTrust {
  public oneTrustPreferencesSet = false;
  public strictlyNecessaryCookiesBlocked = false;
  public performanceAndAnalyticsCookiesBlocked = false;
  public functionalityCookiesBlocked = false;
  public retargetingOrAdvertisingCookiesBlocked = false;
  public locationDataHash: LocationHash = {};
  public userLocatedInEU = false;
  public allCookiesAllowed = false;

  public optanonConsentCookieString = '';
  public optanonAlertBoxClosedCookieString = '';
  public squareGeoCookieString = '';
  public squareGeoCookieCountry = '';
  public squareGeoCookieRegion = '';
  public squareGeoLocationHash: LocationHash = {};
  public squareGeoInEu: boolean | undefined = undefined;
  public artificialIntelligenceFunctionalityBlocked:
    | boolean
    | undefined = undefined;
  public groupConsentHash: Record<string, unknown> = {};
  public defaultConsentRequired = true;
  public accessToDataPrivacyPreferencesRequired = true;
  private _passSquareGeoToOneTrust = false;

  constructor({ passSquareGeoToOneTrust = false } = {}) {
    this._init(passSquareGeoToOneTrust);
  }

  _init(passSquareGeoToOneTrust: boolean): void {
    this._setVars(passSquareGeoToOneTrust);
    this._setGeolocationData();
    this._onUpdatedCookiePreferences();
    this._setAIData();
  }

  // Only setting the variables that can be defined without a network call
  _setVars(passSquareGeoToOneTrust: boolean): void {
    this.optanonConsentCookieString = this._getCookieValue(CONSENT_COOKIE_KEY);
    this.optanonAlertBoxClosedCookieString = this._getCookieValue(
      ALERT_BOX_CLOSED_COOKIE_KEY
    );
    this.squareGeoCookieString = this._getCookieValue(
      CDN_GEOLOCATION_COOKIE_KEY
    );
    this._passSquareGeoToOneTrust = passSquareGeoToOneTrust;
  }

  // Getters

  get isUserLocatedInEU(): boolean {
    if (this.locationDataHash) {
      return this.checkIfUserLocatedInEU(this.locationDataHash.country);
    } else {
      return false;
    }
  }

  get isSquareGeoInEu(): boolean | undefined {
    if (this.squareGeoCookieCountry) {
      return this.checkIfUserLocatedInEU(this.squareGeoCookieCountry);
    }

    return undefined;
  }

  get isartificialIntelligenceFunctionalityBlocked(): boolean {
    const complianceStates: string[] =
      ALTERNATIVE_COMPLIANCE_LAWS.get(this.squareGeoCookieCountry) || [];
    const isCoveredByComplianceLaw = complianceStates.includes(
      this.squareGeoCookieRegion
    );
    return (
      this.isSquareGeoInEu ||
      isCoveredByComplianceLaw ||
      this.retargetingOrAdvertisingCookiesBlocked
    );
  }

  get isAccessToDataPrivacyPreferencesRequired(): boolean {
    const country = this._getCountryInLocationDataHash();
    if (country) {
      return (
        DEFAULT_OPT_IN_REGIONS.includes(country) ||
        DEFAULT_OPT_OUT_REGIONS.includes(country)
      );
    }
    // if no country, default is false
    return false;
  }

  get isDefaultConsentRequired(): boolean {
    const country = this._getCountryInLocationDataHash();
    if (country) {
      return DEFAULT_OPT_OUT_REGIONS.includes(country);
    }
    // if no country, default is true
    return true;
  }

  _getCountryInLocationDataHash(): string | undefined {
    return 'country' in this.locationDataHash
      ? this.locationDataHash.country
      : '';
  }
  // Event Behavior

  _dispatchUpdateEvent(): void {
    const updateEvent = new CustomEvent(
      PREFERENCES_CHANGED_EVENT_KEY,
      this.groupConsentHash
    );
    document.dispatchEvent(updateEvent);
  }

  _onUpdatedCookiePreferences(): void {
    // One Trust sets data to the cookie on every page load. In the majority of
    // cases this is just going to be a timestamp for when the user set their
    // preferences, and the code in this module is only concerned with changes
    // to the preferences of the tracking groups.
    if (this._oneTrustGroupChoicesSet()) {
      this._buildHashOfGroupConsentChoices();
    } else {
      // If the cookie containing the group choices does not exist,
      // use the values existing in OnetrustActiveGroups.
      // There is a race condition for non EU users who are auto-consented.
      // In this state, the cookie will not yet have the consent choices,
      // but the Application layer will. This uses those known values if a cookie is not available.
      this._buildHashOfGroupConsentFromActiveGroups();
    }

    if (Object.keys(this.groupConsentHash).length > 0) {
      // TODO: figure out if we're ok with having the event fire on every
      // page load (which is going to happen because of the changed timestamp)
      // or if we want to add a second cookie for an initial state so that
      // we can compare group preferences against it.
      this._setConsentChoices();
      this._dispatchUpdateEvent();
    }
  }

  // Geolocation behavior

  checkIfUserLocatedInEU(country?: string): boolean {
    const currentCountry = country || '';
    return EU_COUNTRIES.includes(currentCountry);
  }

  _setGeolocationData(): void {
    // squareGeo cookie is either 2 chars long (if no region, e.g. US) or 6 if
    // there is an encoded region (e.g US-CA for United States, California)
    // subdivision country code is up to three chars long https://en.wikipedia.org/wiki/ISO_3166-2
    const validSquareGeoCookieExists =
      this.squareGeoCookieString.length >= 2 &&
      this.squareGeoCookieString.length <= 6;

    if (validSquareGeoCookieExists) {
      this._parseSquareGeoCookieForLocationData();
      this.squareGeoInEu = this.isSquareGeoInEu;
      this.locationDataHash = this.squareGeoLocationHash;
      this.userLocatedInEU = this.squareGeoInEu as boolean;
      this.accessToDataPrivacyPreferencesRequired = this.isAccessToDataPrivacyPreferencesRequired;
      this.defaultConsentRequired = this.isDefaultConsentRequired;
      if (this._passSquareGeoToOneTrust && this.squareGeoCookieCountry) {
        globalThis.OneTrust = globalThis.OneTrust || {
          geolocationResponse: {
            stateCode: this.squareGeoCookieRegion,
            countryCode: this.squareGeoCookieCountry,
          },
        };
      }
    } else if (globalThis.OneTrust) {
      this.locationDataHash = OneTrust.getGeolocationData?.() || {};
      this.userLocatedInEU = this.isUserLocatedInEU;
      this.accessToDataPrivacyPreferencesRequired = this.isAccessToDataPrivacyPreferencesRequired;
      this.defaultConsentRequired = this.isDefaultConsentRequired;
    }
  }

  // Restrict access to AI tools
  _setAIData(): void {
    this.artificialIntelligenceFunctionalityBlocked = this.isartificialIntelligenceFunctionalityBlocked;
  }

  // Cookie and Preference behavior

  _setConsentChoices(): void {
    if (this.optanonAlertBoxClosedCookieString.length > 0) {
      this.oneTrustPreferencesSet = true;
    }
    // TODO: figure out if there are any complexities involved with preference
    // changes here or if we can early return after this.
    if (Object.values(this.groupConsentHash).includes('0')) {
      this.allCookiesAllowed = false;
    } else if (
      Object.values(this.groupConsentHash).every((value) => value === '1') &&
      Object.keys(this.groupConsentHash).length ===
        Object.keys(COOKIE_GROUP_KEYS).length
    ) {
      // The second condition ensures this.allCookiesAllowed is false when this.groupConsentHash = {'C0001': '1'}
      // this happens when this.groupConsentHash is build from OnetrustActiveGroups datalayer
      this.allCookiesAllowed = true;
    }

    // Strictly necessary
    if (this.groupConsentHash[COOKIE_GROUP_KEYS.strictlyNecessary] !== '1') {
      this.strictlyNecessaryCookiesBlocked = true;
    }

    // Performance and Analytics
    if (
      this.groupConsentHash[COOKIE_GROUP_KEYS.performanceAndAnalytics] !== '1'
    ) {
      this.performanceAndAnalyticsCookiesBlocked = true;
    }

    // Functionality
    if (this.groupConsentHash[COOKIE_GROUP_KEYS.functionality] !== '1') {
      this.functionalityCookiesBlocked = true;
    }

    // Retargeting or Advertising
    if (
      this.groupConsentHash[COOKIE_GROUP_KEYS.retargetingOrAdvertising] !== '1'
    ) {
      this.retargetingOrAdvertisingCookiesBlocked = true;
    }
  }

  // Parses the One Trust Cookie to derive the user's actual preferences
  // for sharing certain cookies.
  _buildHashOfGroupConsentChoices(): Record<string, unknown> {
    const cookieDataAsArray = this.optanonConsentCookieString.split('&'); // Data delimited by ampersands
    const groupCookieDataString = cookieDataAsArray.find((dataString) => {
      return dataString.includes(CONSENT_GROUP_COOKIE_SUBSTRING);
    });
    if (!groupCookieDataString) {
      throw new Error('No group cookie data string');
    }
    const groupPreferencesStringAsArray = groupCookieDataString
      .split('=')[1]
      .split(',');
    return (this.groupConsentHash = groupPreferencesStringAsArray.reduce(
      (consentHashObject, groupString) => {
        const splitString = groupString.split(':');
        consentHashObject[splitString[0]] = splitString[1];
        return consentHashObject;
      },
      this.groupConsentHash
    ));
  }

  // Parses the application layer active groups object to find the user's consent choices
  // Will be used if the user is outside of the EU and has not yet auto-consented.
  _buildHashOfGroupConsentFromActiveGroups(): void {
    const activeGroupsString = globalThis.OnetrustActiveGroups || '';
    const activeGroups = activeGroupsString.split(',');

    activeGroups.forEach((consentChoice: string) => {
      if (consentChoice !== '') {
        this.groupConsentHash[consentChoice] = '1';
      }
    });
  }

  // Gets cookie value by iterating through different cookie keys in document.cookie
  _getCookieValue(cookieKey: string): string {
    if (typeof document === 'undefined') {
      return '';
    }

    // To prevent the for loop in the first place assign an empty array
    // in case there are no cookies at all.
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (const cooky of cookies) {
      const parts = cooky.split('=');
      let value = parts.slice(1).join('=');

      if (value[0] === '"') {
        value = value.slice(1, -1);
      }

      try {
        const foundKey = parts[0];
        if (foundKey === cookieKey) {
          return decodeURIComponent(value);
        }
      } catch (e) {
        console.log(e);
      }
    }

    return '';
  }

  _parseSquareGeoCookieForLocationData(): void {
    const cookieParts = this.squareGeoCookieString.split('-');

    this.squareGeoCookieCountry = cookieParts[0] || '';
    this.squareGeoCookieRegion = cookieParts[1] || '';
    // We're using the cookieparts directly here because we actually want an
    // undefined value for these properties if they don't exist to keep parity
    // with the way One Trust handles the data.
    this.squareGeoLocationHash = {
      country: cookieParts[0],
      state: cookieParts[1],
    };
  }

  _oneTrustGroupChoicesSet(): boolean {
    return this.optanonConsentCookieString.includes(
      CONSENT_GROUP_COOKIE_SUBSTRING
    );
  }
}
